import type { RecentlyViewedProduct } from './index';

import { parseFormattedPrice } from './parseFormattedPrice';
import { ProductMediaTypeEnum } from '../../types/algolia';
import type { AlgoliaProduct } from '~/components/shared/recommendations/tile/types';
import type { MultiSigningJSON, ProductHit } from '~/types/algolia';
import type { StyleVariant } from '~/utilities/graphql/codegen';

import { getLocalizedData } from './index';

export const mapProductToAlgolia = (
  product: RecentlyViewedProduct,
  lang: string
): AlgoliaProduct => {
  const {
    name,
    url,
    color,
    code,
    materialGroup,
    baseProduct,
    signings,
    price,
    fromPrice,
    intermediaryPrice,
    simplifiedImages,
    fiberRanking,
    stockQuantity,
    mccValue,
    isGiftCard,
    staticCategoryPathIds,
    comingSoon,
    formattedLowestPrice,
    personalisedPrice,
    priceDisplayMode,
  } = product;

  const { productFiberRankingData } = fiberRanking?.fiberRanking ?? {};

  return {
    url,
    code,
    stockQuantity,
    mccValue,
    isGiftCard,
    staticCategoryPathIds,
    comingSoon,
    name: getLocalizedData(lang, name),
    exactColorDescription: getLocalizedData(lang, color?.description),
    materialgroup: getLocalizedData(lang, materialGroup),
    'baseProduct.code': baseProduct ?? '',
    multiSigningJson: signings?.map(
      signing =>
        ({
          ...signing,
        } as MultiSigningJSON)
    ),
    priceValue: price?.value,
    fromPriceValue: fromPrice?.value,
    intermediaryPriceValue: intermediaryPrice?.value,
    personalisedPriceValue: personalisedPrice?.value,
    lowestPrice: formattedLowestPrice
      ? parseFormattedPrice(formattedLowestPrice as string)
      : undefined,
    priceDisplayMode,
    simplifiedImageJson: Object.fromEntries(
      simplifiedImages?.entry
        ?.filter(image => image?.value)
        ?.map(image => [image?.key, { url: image?.value?.url }]) ?? []
    ),
    ...(productFiberRankingData && {
      fiberRankingScore: productFiberRankingData?.score,
      fiberRanking: productFiberRankingData.ranking,
      fiberCompositionsJson: productFiberRankingData.fibers,
    }),
  } as AlgoliaProduct;
};

export const mapVariantsToAlgolia = (variant: StyleVariant): ProductHit => {
  const {
    productUrl,
    name,
    formattedLowestPrice,
    unformattedBasePrice,
    unformattedFromPrice,
    unformattedIntermediaryPrice,
    signing,
    images,
    facetColorStyle,
    color,
  } = variant;

  const priceValue = parseFloat(unformattedBasePrice || '0');
  const fromPriceValue = parseFloat(unformattedFromPrice || '0') || priceValue;
  const intermediaryPriceValue = parseFloat(unformattedIntermediaryPrice || '0');

  return {
    name,
    facetColorStyle,
    priceValue,
    fromPriceValue,
    intermediaryPriceValue,
    url: productUrl,
    lowestPrice: formattedLowestPrice
      ? parseFormattedPrice(formattedLowestPrice as string)
      : undefined,
    multiSigningJson: signing
      ? [
          {
            ...signing,
            productSigningBackgroundColour:
              signing?.productSigningBackgroundColour ?? signing?.backgroundColour,
            productSigningTextColour: signing?.productSigningTextColour ?? signing?.textColour,
            labelCssClasses: signing?.cssClasses,
          },
        ]
      : [],
    simplifiedImageJson: {
      [ProductMediaTypeEnum.E01]: {
        url: images?.primaryImage?.replace('h_147', 'h_1038'),
      },
      [ProductMediaTypeEnum.E02]: {
        url: images?.primaryImage?.replace('h_147', 'h_1038'),
      },
    },
    swatch: images?.swatch,
    exactColorDescription: color,
  } as unknown as AlgoliaProduct;
};
